import React, { useEffect, useState } from 'react';
import { Button, Modal, Table, Tooltip } from 'antd';
import 'antd/dist/reset.css';
import { ProfileOutlined } from '@ant-design/icons';
import CKETextContent from 'views/Segments/CKETextContent/CKETextContent';
import { useSelector } from 'react-redux';
import RenderHtml from 'components/UI/RenderHtml/RenderHtml';
import { activeAdjustedExperienceSelector } from 'views/Experiences/selector';
import "./TaskSpecificClarification.scss";
import { showConfirmModal } from 'components/UI/Segment/UIHelper';
import { cloneDeep, isEmpty, orderBy } from 'lodash';
import { useTranslate } from 'lib/MrTranslate/MrTranslate';
import { getStrandsFromRubricsWithUpdatedCA } from 'lib/utils/helperMethods';

const RenderLevelDescriptors = (props) => {
  const { item = {}, rowIndex, hl = '', onTextChanged, colIdx, editAllowed, status, errorIndexes, criteriaTitle, itemTitle, openTSCModal, caId } = props;
  const { level_descriptors = [] } = item;
  const levelDescriptor = level_descriptors.find(ld => ld.level == hl.charAt(hl.length - 1));
  const textContentToRender = levelDescriptor ? levelDescriptor.desc : '';
  const key = `${colIdx}-${rowIndex}-${criteriaTitle}-${itemTitle}-${openTSCModal}`
  const [editMode, setEditMode] = useState(false);

  return (
    editMode ?
      <>
        <div key={`ckeditor-task-specific-clarification-${key}`} className='ckeditor-task-specific-clarification'>
          <CKETextContent
            ckeConfig={
              { 
                debounceInterval: 1,
                autoFocus: true,
                autoFocusAtEnd: true,
                toolbar: ['bold', 'italic', 'underline'],
              }
            }
            placeholder={'*Required'}
            itemId={`${key}-ckeditor`}
            value={textContentToRender}
            onChange={(data) => {
              onTextChanged(data, item.label, colIdx, rowIndex, criteriaTitle, itemTitle, caId)
            }}
            onBlur={() => setEditMode(false) }
          />
        </div>
      </> : (
        <div key={`ckeditor-enable-on-edit-mode-${key}`} className={`${status === "published" ? 'cursor-default' : 'cursor-pointers'} column-read-only-mode`}
          onClick={() => {
            if (status === "published") {
              if (editAllowed) {
                setEditMode(false)
              }
            } else {
              if (editAllowed) {
                setEditMode(true)
              }
            } 
          }}
        >
          {textContentToRender ? <RenderHtml text={textContentToRender} /> : <span className='required'>*Required</span>}
        </div>
      )
  )
}

const TaskSpecificClarification = (props) => {

  const { updateResource = null, editAllowed = false, className, showTSCInActions = false} = props;
  
  const experience = useSelector(activeAdjustedExperienceSelector())
  const [localCriteriumAssociations, setLocalCriteriumAssociations] = useState([]);
  const [openTSCModal, setOpenTSCModal] = useState(false);
  const [errorIndexes, setErrorIndexes] = useState({});

  const { status, criterium_associations = [], settings = {}, rubric = {}, relation_items = [] } = experience
  const mrIntl = useTranslate()
  const experienceSettings = settings
  const tscEnabled = experienceSettings.tsc_enabled

  // To enable or disable a Task Specific Clarification button based on whether strands are present
  let isStrandsExist = false;
  criterium_associations.forEach(ca => {
    if (ca.strands.length > 0) {
      isStrandsExist = true;
      return true;
    }
    return false;
  });

  let tscButtonTooltip = ''
  
  if (!isStrandsExist) {
    tscButtonTooltip = 'Select a strand in any question to enable Task Specific Clarification'
  } else if (isStrandsExist && !showTSCInActions) {
    tscButtonTooltip = 'Task Specific Clarification'
  }

  let tscButtonTooltipPlacement = 'bottom'
  
  if (showTSCInActions && (!isStrandsExist || tscEnabled)) {
    tscButtonTooltipPlacement = 'left'
  }

  let tscButtonDisable = false

  if(!isStrandsExist || (tscEnabled && showTSCInActions)) {
    tscButtonDisable = true
  }

  const showTSCModal = (action) => {
    let newLocalCriteriumAssociations = action === "initialize" ? getStrandsFromRubricsWithUpdatedCA(criterium_associations, rubric.criteria, relation_items) : cloneDeep(criterium_associations)
    setLocalCriteriumAssociations(newLocalCriteriumAssociations)
    setOpenTSCModal(!openTSCModal);
  };

  const handleRemove = () => {
    showConfirmModal({
      title: mrIntl("TaskSpecificClarification.remove_task_specific_clarification"),
      content: mrIntl("TaskSpecificClarification.remove_task_specific_clarification_from_this_assessment_are_you_sure"),
      onOk: () => {
        const updatedCriteriumAssociations = cloneDeep(localCriteriumAssociations)
        updatedCriteriumAssociations.forEach(ca => {
          ca.strands && ca.strands.forEach(s => {
            delete s.level_descriptors
            delete s.title
            delete s.desc
          })
        })
        let newExperienceSettings = { ...experienceSettings };
        if (tscEnabled) {
          newExperienceSettings.tsc_enabled = false;
        }

        updateResource(
          {
            uid: experience.uid,
            criterium_associations_attributes: updatedCriteriumAssociations,
            settings: newExperienceSettings
          },
          {
            successCallback: () => {
              handleCloseModal()
              setLocalCriteriumAssociations([])

            },
            success: {
              showMessage: true,
              message: mrIntl("TaskSpecificClarification.task_specific_clarification_removed_successfully"),
            },
            error: {
              showMessage: true,
              message: mrIntl("TaskSpecificClarification.error_in_removing_task_specific_clarification"),
            },
          }
        );
      },
      okText: 'Remove',
      cancelText: 'Cancel',
    });
  }

  const handleCloseModal = () => {
    setErrorIndexes({})
    setOpenTSCModal(false)
  }

  const onTextChanged = (data, label, colIdx, rowIndex, criteriaTitle, itemTitle, caId) => {
    console.log('onTextChanged ===>>', colIdx, label, data, errorIndexes, itemTitle);
    const errorKey = `${colIdx}-${rowIndex}-${criteriaTitle}-${itemTitle}`
    let newLocalCriteriumAssociations = [...localCriteriumAssociations]
    const currentCAIndex = newLocalCriteriumAssociations.findIndex(ca => ca.id === caId);
    const currentCA = newLocalCriteriumAssociations[currentCAIndex] || {};
    const currentCAStrands = currentCA.strands || [];
    const currentStrand = currentCAStrands.find(s => s.label === label) || {};
    const currentStrandLevelDescriptors = currentStrand.level_descriptors || [];

    if (data.trim() === '') {
      setErrorIndexes({ ...errorIndexes, [errorKey]: '*Required' })
    } else {
      const newErrorIndexes = { ...errorIndexes }
      if (newErrorIndexes.hasOwnProperty(errorKey)) {
        delete newErrorIndexes[errorKey];
        setErrorIndexes(newErrorIndexes)
      }
    }
    if (currentStrandLevelDescriptors.length > 0) {
      if (colIdx === 0) {
        currentStrandLevelDescriptors[colIdx].desc = data
      } else {
        // descriptor.desc both data should be update when i edit (like- ["0", "1-2", "3-4", "5-6", "7-8"])
        // we are showing indexes 0,2,4,6,8 in table and the description is same for index
        // The index descriptions are the same for indices: ["0", "1-2", "3-4", "5-6", "7-8"]. When updating index 2, we also need to update index 1 because the first index is not displayed in the table. In the table, only indices [0, 2, 4, 6, 8] are shown, so when updating these indices, we also need to update [1, 3, 5, 7].
        currentStrandLevelDescriptors[colIdx + colIdx].desc = data
        currentStrandLevelDescriptors[colIdx + colIdx - 1].desc = data
      }
    }

    setLocalCriteriumAssociations(newLocalCriteriumAssociations)
  }

  const renderColumn = (title, dataIndex, key, renderFunction) => ({
    title,
    dataIndex,
    key,
    scope: 'col',
    render: renderFunction
  });

  const renderCriterionColumns = (headerLabels, onTextChanged, criteriaTitle, caId) => {
    const gridColumns = [
      renderColumn("", "dataIndex", "key-1", (_, item) => (
        <p className='criterion-columns-row' key={`render-criterion-title-desc-${item.title}`} >{item.title}. {item.desc}</p>
      ))
    ];

    headerLabels.forEach((hl, colIdx) => {
      gridColumns.push(
        renderColumn(hl, colIdx, colIdx, (_, item, rowIndex) => (
          <RenderLevelDescriptors key={`render-level-descriptors-${colIdx}-${rowIndex}-${criteriaTitle}-${item.title}-${openTSCModal}`} status={status} item={item} rowIndex={rowIndex} hl={hl} onTextChanged={onTextChanged} colIdx={colIdx} editAllowed={editAllowed} errorIndexes={errorIndexes} criteriaTitle={criteriaTitle} itemTitle={item.title} openTSCModal={openTSCModal} caId={caId}/>
        ))
      );
    });

    return gridColumns;
  };

  const renderCriterionTable = (ca = {}, finalStrands, onTextChanged) => {
    const { label, title } = ca.custom_fields

    return finalStrands.length ? (
      <div key={`render-table-criteria-title-lable-${title}`} >
        <div key={`render-table-criteria-title-lable-${label}`}>
          <h2 key={`criterion-${title}-${label}-title-label`} className='criterion-title-label'>
            Criterion {title}: {label}
          </h2>
        </div>
        <Table
          key={`strand-show-table-${ca.title}`}
          className='task-specific-clarification-table'
          columns={renderCriterionColumns(["0", "1-2", "3-4", "5-6", "7-8"], onTextChanged, title, ca.id)}
          dataSource={finalStrands}
          pagination={false}
        />
      </div>
    ) : null

  }

  const renderCriteriaTables = (localCriteriumAssociations, onTextChanged) => {
    const tables = [];
    localCriteriumAssociations.forEach((ca) => {
      const orderedStrands = orderBy(ca.strands, ["label"], ["asc"])
      tables.push(renderCriterionTable(ca, orderedStrands, onTextChanged))
    });
    return tables;
  }

  const handleSave = () => {
    let newExperienceSettings = { ...experienceSettings };
    if (!tscEnabled) {

      newExperienceSettings.tsc_enabled = true;
    }

    updateResource(
      {
        uid: experience.uid,
        criterium_associations_attributes: localCriteriumAssociations,
        settings: newExperienceSettings
      },
      {
        successCallback: () => {
          setLocalCriteriumAssociations([])
          handleCloseModal()
        },
        success: {
          showMessage: true,
          message: mrIntl("TaskSpecificClarification.task_specific_clarification_updated_successfully"),
        },
        error: {
          showMessage: true,
          message: mrIntl("TaskSpecificClarification.error_in_updating_task_specific_clarification"),
        },
      }
    );
  }


  return (
    <>
      {(showTSCInActions || tscEnabled) && (
        <>
          <div key={'task-specific-clarification-modal'} className='task-specific-clarification' >
          
            <Tooltip placement={tscButtonTooltipPlacement} title={tscButtonTooltip}>
              <Button key={'task-specific-clarification-button'} icon={<ProfileOutlined />} disabled={tscButtonDisable} type={showTSCInActions ? 'text' : 'default'} shape={!showTSCInActions && 'circle'} className={`ant-btn-circle task-specific-clarification-button ${className}`} onClick={() => showTSCModal(showTSCInActions ? "initialize" : "")} >{showTSCInActions && 'Add Task Specific Clarification'}</Button>
            </Tooltip>

            <Modal
              title='Task Specific Clarification'
              key={'task-specific-clarification-table-modal'}
              className='task-specific-clarification-modal'
              width="90%"
              height='80%'
              onCancel={() => handleCloseModal()}
              open={openTSCModal}
              okText='Save'
              footer={status === "draft" && (
                <>
                  <Button disabled={!tscEnabled} key="remove-tsc" onClick={() => handleRemove()}>
                    Remove
                  </Button>
                  <Button disabled={!isEmpty(errorIndexes)} key="add-tsc" type="primary" onClick={() => handleSave()}>
                    {tscEnabled ? 'Save' : 'Add'}
                  </Button>
                </>
              )}
            >
              {renderCriteriaTables(localCriteriumAssociations, onTextChanged)}
            </Modal>

          </div>
        </>
      )
      }
    </>
  );
};

export default TaskSpecificClarification;
